import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, addDoc, serverTimestamp, doc, getDoc, Timestamp } from 'firebase/firestore';
import { createIncidentWithBlankStaff } from '../utils/incidentManagement';
import { Form, Input, Select, Button, Space, Card, Row, Col, Typography, message, DatePicker } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import { logActivity, ACTIVITY_TYPES, createIncidentLog } from '../utils/activityLogger';
import '../styles/NewIncident.css';
import { INCIDENT_TYPES } from '../constants/incidentTypes';
import { createMessageAuthorName } from '../utils/userDisplayUtils';
import TimeInput from '../components/TimeInput';
import RegulatoryTriggerSelector from '../components/RegulatoryTriggerSelector';
import CrisisTaskPreviewModal from '../components/CrisisTaskPreviewModal';
import { generateCrisisTasks, convertToFirestoreTask } from '../utils/crisisTaskEngine';
import { getAvailableResourcesForCrisisType } from '../utils/crisisResourceMapping';
import geocodingService from '../services/geocodingService';
import LocationInput from '../components/LocationInput';

const SEVERITY_LEVELS = ['Low', 'Moderate', 'High', 'Extreme'];
const STATUS_OPTIONS = ['Active', 'Stabilising', 'Resolved', 'Exercise'];

// Add new activity types
const INCIDENT_ACTIVITY_TYPES = {
  ...ACTIVITY_TYPES,
  INCIDENT_CREATED: 'incident_created',
  INCIDENT_DELETED: 'incident_deleted',
  INCIDENT_STATUS_CHANGED: 'incident_status_changed',
  INCIDENT_SEVERITY_CHANGED: 'incident_severity_changed',
  INCIDENT_LOCATION_CHANGED: 'incident_location_changed',
  INCIDENT_OCCURRENCE_TIME_CHANGED: 'incident_occurrence_time_changed'
};

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Helper function to map sender to CMT role (copied from RegulatoryComplianceTab)
const mapSenderToCmtRole = (sender) => {
  const senderLower = sender.toLowerCase();
  
  // Map common sender titles to CMT roles
  if (senderLower.includes('ciso') || senderLower.includes('security') || senderLower.includes('cyber')) {
    return 'safety_security';
  }
  if (senderLower.includes('ceo') || senderLower.includes('leader') || senderLower.includes('director')) {
    return 'cmt_leader';
  }
  if (senderLower.includes('legal') || senderLower.includes('counsel')) {
    return 'legal';
  }
  if (senderLower.includes('hr') || senderLower.includes('human') || senderLower.includes('people')) {
    return 'talent_hr';
  }
  if (senderLower.includes('it') || senderLower.includes('tech') || senderLower.includes('technology')) {
    return 'it_tech';
  }
  if (senderLower.includes('finance') || senderLower.includes('cfo') || senderLower.includes('financial')) {
    return 'finance';
  }
  if (senderLower.includes('comms') || senderLower.includes('communication') || senderLower.includes('media') || senderLower.includes('pr')) {
    return 'comms_media';
  }
  if (senderLower.includes('facilities') || senderLower.includes('operations')) {
    return 'facilities';
  }
  
  // Default to CMT Leader if no specific match
  return 'cmt_leader';
};

// Helper function to convert time units to milliseconds
const getTimeMultiplier = (unit) => {
  switch (unit.toLowerCase()) {
    case 'immediate':
      return 15 * 60 * 1000; // 15 minutes for immediate notifications
    case 'minutes':
      return 60 * 1000;
    case 'hours':
      return 60 * 60 * 1000;
    case 'days':
      return 24 * 60 * 60 * 1000;
    case 'weeks':
      return 7 * 24 * 60 * 60 * 1000;
    default:
      return 60 * 60 * 1000; // Default to hours
  }
};

export default function NewIncident() {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCmt, availableCmts, isMultiCmtEnabled } = useMultiCmt();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [regulatoryRules, setRegulatoryRules] = useState([]);
  const [selectedRegulatoryTriggers, setSelectedRegulatoryTriggers] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);
  
  // Crisis report promotion detection
  const crisisReport = location.state?.crisisReport;
  const fromReportId = location.state?.fromReportId;
  const isPromotingFromReport = Boolean(crisisReport || fromReportId);
  
  // Crisis task generation state
  const [showTaskPreview, setShowTaskPreview] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState([]);
  const [taskGenerationLoading, setTaskGenerationLoading] = useState(false);
  const [createdIncidentId, setCreatedIncidentId] = useState(null);

  // Function to create regulatory notification tasks
  const createRegulatoryTasks = async (incidentId, triggers, incidentData) => {
    if (!triggers || triggers.length === 0) {
      console.log('No regulatory triggers to create tasks for');
      return;
    }

    console.log('Creating regulatory tasks for triggers:', triggers);

    // Create tasks for all selected triggers
    for (const trigger of triggers) {
      const rule = regulatoryRules.find(r => r.trigger === trigger);
      if (!rule) {
        console.warn(`No regulatory rule found for trigger: ${trigger}`);
        continue;
      }

      const assignedRole = mapSenderToCmtRole(rule.sender);
      
      // Calculate due date based on whether it's immediate notification or regular deadline
      const dueDate = rule.immediateNotificationRequired || rule.deadlineUnit === 'immediate' ? 
        // For immediate notifications, set deadline to incident occurrence time (or now if no occurrence time)
        incidentData.occurredAt ? 
          new Date(incidentData.occurredAt.toDate().getTime()) : // Same time as incident
          new Date(Date.now()) : // Right now if no incident time
        // For regular deadlines, calculate from incident occurrence time
        incidentData.occurredAt ? 
          new Date(incidentData.occurredAt.toDate().getTime() + (parseInt(rule.deadlineValue) * getTimeMultiplier(rule.deadlineUnit))) :
          new Date(Date.now() + (parseInt(rule.deadlineValue) * getTimeMultiplier(rule.deadlineUnit)));

      const taskData = {
        name: `${rule.immediateNotificationRequired ? '🚨 URGENT: ' : ''}Notify ${rule.regulator}`,
        title: `${rule.immediateNotificationRequired ? '🚨 URGENT: ' : ''}Notify ${rule.regulator}`,
        description: `${rule.immediateNotificationRequired ? '🚨 IMMEDIATE NOTIFICATION REQUIRED\n\n' : ''}Regulatory notification required: ${rule.trigger}\n\nRegulator: ${rule.regulator}\nMethod: ${Array.isArray(rule.method) ? rule.method.join(', ') : rule.method}\nSender: ${rule.sender}\nDeadline: ${rule.deadlineValue} ${rule.deadlineUnit}${rule.immediateNotificationRequired ? '\n\n⚠️ This notification requires immediate action regardless of the deadline.' : ''}`,
        incidentId: incidentId,
        orgId: currentUser.orgId,
        assignedRole: assignedRole,
        owner: null, // ✅ FIX: No owner for auto-created regulatory tasks
        status: 'not_started',
        dueDate: rule.immediateNotificationRequired ? new Date(Date.now() + (15 * 60 * 1000)) : dueDate, // 15 minutes for immediate notifications
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        autoCreated: true, // ✅ FIX: Flag for system-generated tasks
        type: 'regulatory_notification',
        regulatoryTrigger: trigger,
        regulator: rule.regulator,
        notificationMethod: rule.method,
        isUrgent: rule.immediateNotificationRequired || false,
        priority: rule.immediateNotificationRequired ? 'critical' : 'normal',
        deleted: false // 🔧 CRITICAL FIX: Required for TasksTab query filtering
      };

      try {
        await addDoc(collection(db, 'c3_tasks'), taskData);

      } catch (error) {
        console.error(`❌ Error creating task for ${rule.regulator}:`, error);
      }
    }
  };

  const [incident, setIncident] = useState({
    title: '',
    crisisType: '',
    location: '',
    status: 'Active',
    severity: 'Low',
    summary: '',
    createdAt: new Date(),
    createdBy: currentUser?.uid,
    createdByName: '', // Will be set during submission
    orgId: currentUser?.orgId,
    occurredAt: null, // When the incident actually occurred
    occurredDate: null, // Date component
    occurredTime: null  // Time component
  });
  const [otherCrisisType, setOtherCrisisType] = useState('');

  // 🎯 PRE-FILL FROM CRISIS REPORT - Map crisis report data to incident form
  useEffect(() => {
    console.log('🔍 NewIncident useEffect triggered:', {
      isPromotingFromReport,
      hasCrisisReport: !!crisisReport,
      locationState: location.state
    });
    
    if (isPromotingFromReport && crisisReport) {
      console.log('🚀 Pre-filling incident form from crisis report:', crisisReport);
      console.log('🕐 Crisis report timestamp fields:', {
        occurredAt: crisisReport.occurredAt,
        receivedAt: crisisReport.receivedAt,
        createdAt: crisisReport.createdAt,
        clientCreatedAt: crisisReport.clientCreatedAt
      });
      console.log('🔍 All crisis report fields:', Object.keys(crisisReport));
      
      // Helper function to map severity
      const mapSeverity = (reportSeverity) => {
        switch (reportSeverity) {
          case 'Low': return 'Low';
          case 'Med': return 'Moderate'; 
          case 'High': return 'High';
          case 'Critical': return 'Extreme';
          default: return 'Moderate';
        }
      };

      // Helper function to format date/time from ISO string or Firestore Timestamp
      const formatDateTime = (timestamp) => {
        if (!timestamp) return { date: null, time: null };
        
        let date;
        
        // Handle different timestamp formats
        if (typeof timestamp === 'string') {
          // ISO string format
          date = new Date(timestamp);
        } else if (timestamp && typeof timestamp.toDate === 'function') {
          // Firestore Timestamp object
          date = timestamp.toDate();
        } else if (timestamp && timestamp.seconds) {
          // Raw Firestore Timestamp object {seconds, nanoseconds}
          date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
        } else if (timestamp instanceof Date) {
          // Already a Date object
          date = timestamp;
        } else {
          console.warn('Unknown timestamp format:', timestamp);
          return { date: null, time: null };
        }
        
        // Format date as YYYY-MM-DD for HTML date input (browser will display as "6 September 2025")
        const dateStr = date.toISOString().split('T')[0]; // "2025-09-06"
        
        // Format time as HH:mm (24-hour format for input field)
        const timeStr = date.toTimeString().split(' ')[0].substring(0, 5); // HH:mm
        
        console.log('🕐 Formatted timestamp:', { original: timestamp, date: dateStr, time: timeStr });
        return { date: dateStr, time: timeStr };
      };

      // Try different timestamp fields in order of preference
      const timestampToUse = crisisReport.occurredAt || 
                            crisisReport.receivedAt || 
                            crisisReport.clientCreatedAt || 
                            crisisReport.createdAt;
      
      console.log('🕐 Using timestamp:', timestampToUse);
      const { date: occurredDate, time: occurredTime } = formatDateTime(timestampToUse);

      // Helper function to map crisis report category to incident crisis type
      const mapCrisisType = (category) => {
        const categoryMap = {
          'active_threat': 'Active Threat',
          'fire': 'Fire',
          'medical': 'Medical Emergency',
          'flood': 'Flood',
          'bomb_threat': 'Suspicious Item / Bomb Threat',
          'kidnap': 'Kidnapping',
          'major_vehicle': 'Vehicle Accident'
        };
        const mappedType = categoryMap[category] || '';
        console.log('🎯 Mapping crisis type:', { category, mappedType });
        return mappedType;
      };

      setIncident(prev => {
        const newIncidentData = {
          ...prev,
          title: crisisReport.summary || `Crisis Report ${crisisReport.shortRef}`,
          crisisType: mapCrisisType(crisisReport.category),
          location: crisisReport.siteName || '',
          severity: mapSeverity(crisisReport.severityGuess),
          summary: crisisReport.details || `Summary: ${crisisReport.summary || ''}`,
          occurredDate: occurredDate,
          occurredTime: occurredTime,
          // Add source tracking
          sourceType: 'crisis_report',
          sourceCrisisReportId: crisisReport.id
        };
        
        console.log('🎯 Setting incident state with:', newIncidentData);
        return newIncidentData;
      });
    }
  }, [isPromotingFromReport, crisisReport]);

  // 🎯 MEMOIZED LOCATION HANDLER - Prevents re-renders that cause focus loss
  const handleLocationChange = useCallback((e) => {
    const newLocation = e.target.value;
    setIncident(prevIncident => ({
      ...prevIncident,
      location: newLocation
    }));
  }, []);

  // Fetch organization's regulatory rules and digital products
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (currentUser?.orgId && db) {
        try {
          const orgDocRef = doc(db, 'c3_organisations', currentUser.orgId);
          const orgDoc = await getDoc(orgDocRef);
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            setRegulatoryRules(orgData.regulatoryRules || []);
            setOrganizationData(orgData);
          }
        } catch (error) {
          console.error('Error fetching organization data:', error);
        }
      }
    };
    fetchOrganizationData();
  }, [currentUser?.orgId, db]);

  // Handle task preview modal confirmation
  const handleTaskConfirmation = async (selectedTasks) => {
    if (!createdIncidentId) {
      console.error('No incident ID available for task creation');
      navigate('/incidents');
      return;
    }

    setTaskGenerationLoading(true);
    try {
      console.log(`Creating ${selectedTasks.length} confirmed tasks...`);
      
      // Create tasks in Firestore
      const taskPromises = selectedTasks.map(task => {
        const firestoreTask = convertToFirestoreTask(task, createdIncidentId, currentUser.orgId, currentUser.uid);
        return addDoc(collection(db, 'c3_tasks'), firestoreTask);
      });
      
      await Promise.all(taskPromises);

      
      message.success(`Added ${selectedTasks.length} crisis response tasks to timeline`);
      
    } catch (error) {
      console.error('Error creating confirmed tasks:', error);
      message.error('Failed to create some tasks. Please check the Tasks tab and add manually if needed.');
    } finally {
      setTaskGenerationLoading(false);
      setShowTaskPreview(false);
      navigate(`/incidents/${createdIncidentId}`);
    }
  };

  // Handle task preview modal close (skip tasks)
  const handleTaskPreviewClose = () => {
    setShowTaskPreview(false);
    navigate(`/incidents/${createdIncidentId}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission
    setLoading(true);
    setError(null);

    try {
      // Get proper display name using universal utility
      let createdByName = 'Unknown User';
      if (currentUser?.uid && db) {
        try {
          const userRef = doc(db, 'c3_users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            createdByName = createMessageAuthorName(userSnap.data());
          } else {
            createdByName = currentUser.displayName || currentUser.email || 'Unknown User';
          }
        } catch (error) {
          console.error('Error fetching user data for incident creator:', error);
          createdByName = currentUser.displayName || currentUser.email || 'Unknown User';
        }
      }

      // Calculate occurredAt timestamp from date and time
      let occurredAt = null;
      if (incident.occurredDate && incident.occurredTime) {
        // Parse the time string (HH:mm format)
        const timeParts = incident.occurredTime.split(':');
        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);
        
        const occurredMoment = moment(incident.occurredDate)
          .hour(hours)
          .minute(minutes)
          .second(0)
          .millisecond(0);
        occurredAt = Timestamp.fromDate(occurredMoment.toDate());
      }

      // Calculate suggested resources for this crisis type
      const crisisTypeForMapping = incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType;
      
      // Handle Enterprise Pro organizations - they have access to all products
      let organizationProducts = organizationData?.digitalProducts || [];
      if (organizationData?.tier === 'enterprise_pro' && organizationProducts.length === 0) {
        // Enterprise Pro has access to all products, populate with all product IDs
        const { DIGITAL_PRODUCTS } = await import('../models/digitalProducts.js');
        organizationProducts = Object.values(DIGITAL_PRODUCTS).map(product => product.id);
        console.log('🏢 Enterprise Pro org detected, using all products:', organizationProducts);
      }
      
      const suggestedResources = getAvailableResourcesForCrisisType(
        crisisTypeForMapping,
        organizationProducts
      );
      
      console.log('🎯 Crisis type:', crisisTypeForMapping);
      console.log('🎯 Organization products:', organizationProducts);
      console.log('🎯 Suggested resources:', suggestedResources);

      // 🎯 MODERNIZED: Use activeCmt from MultiCmtContext
      const resolvedCmtId = activeCmt?.id || null;
      if (!resolvedCmtId) {
        // Enhanced debugging for CMT selection issue
        console.error('🚨 CMT SELECTION DEBUG:', {
          activeCmt: activeCmt,
          availableCmts: availableCmts,
          isMultiCmtEnabled: isMultiCmtEnabled,
          currentUser: {
            uid: currentUser?.uid,
            orgId: currentUser?.orgId,
            role: currentUser?.role
          }
        });
        
        if (availableCmts && availableCmts.length === 0) {
          message.error('You are not a member of any Crisis Management Team. Please contact your administrator.');
        } else if (availableCmts && availableCmts.length === 1) {
          message.error('CMT auto-selection failed. Please contact technical support.');
        } else {
          message.error('Please select a Crisis Management Team before creating an incident.');
        }
        setLoading(false);
        return;
      }

      // Prepare base incident data
      const baseIncidentData = {
        title: incident.title,
        crisisType: incident.crisisType, // Always keep the selected type from dropdown
        customCrisisType: incident.crisisType === 'Other' ? otherCrisisType : null, // Store custom type separately
        location: incident.location || 'Unknown',
        status: 'Active',
        severity: incident.severity || 'Low',
        summary: incident.summary || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        createdByName: createdByName,
        orgId: currentUser.orgId,
        cmtId: resolvedCmtId,
        cmtName: activeCmt?.name || null,
        occurredAt: occurredAt, // When the incident actually occurred
        selectedRegulatoryTriggers: selectedRegulatoryTriggers, // User-confirmed regulatory triggers
        suggestedResources: suggestedResources // Available digital resources for this crisis type
      };

      // 🗺️ AUTOMATIC GEOCODING: Try to geocode the location if provided
      let incidentData = { ...baseIncidentData };
      
      if (incident.location && incident.location.trim().length > 3) {
        console.log('🗺️ Attempting automatic geocoding for crisis location:', incident.location);
        try {
          const geocodingResult = await geocodingService.geocodeCrisisLocation(incident.location);
          
          if (geocodingResult.success) {

            incidentData = {
              ...incidentData,
              latitude: geocodingResult.latitude,
              longitude: geocodingResult.longitude,
              formattedLocation: geocodingResult.formattedAddress,
              geocodedAt: serverTimestamp(),
              geocodingSource: geocodingResult.source
            };

            // 🔄 PHASE 1 ENHANCEMENT: Store reverse geocoded name if available
            if (geocodingResult.reverseGeocodeName) {
              incidentData.reverseGeocodeName = geocodingResult.reverseGeocodeName;
            }
            
            // Show success message to user
            message.success(`Location "${incident.location}" mapped successfully`);
          } else {
            console.warn('⚠️ Crisis location geocoding failed:', geocodingResult.error);
            // Don't block incident creation on geocoding failure
            message.warning(`Location "${incident.location}" could not be mapped automatically. You can update coordinates later.`);
          }
        } catch (geocodingError) {
          console.error('❌ Crisis geocoding error:', geocodingError);
          // Don't block incident creation on geocoding failure
          message.warning('Location geocoding failed. You can update coordinates later.');
        }
      }

      console.log('🚀 PHASE 1: Creating incident with data:', incidentData);
      console.log('🚀 Is promoting from report:', isPromotingFromReport);
      console.log('🚀 Crisis report present:', !!crisisReport);
      console.log('🚀 From report ID:', fromReportId);

      // Use the inheritance-bug-fixed creation function  
      const docId = await createIncidentWithBlankStaff(db, currentUser.orgId, incidentData);
      console.log('✅ PHASE 1 COMPLETE: Incident created with ID:', docId);
      
      // Store incident ID for task generation
      setCreatedIncidentId(docId);

      // 🔗 PHASE 2: Link crisis report if promoting from report
      if (isPromotingFromReport && (crisisReport || fromReportId)) {
        try {
          console.log('🚀 PHASE 2: Linking crisis report to new incident...');
          console.log('🔗 Crisis report data available:', !!crisisReport);
          console.log('🔗 Crisis report ID:', crisisReport?.id || fromReportId);
          
          // Import crisis report service
          const { default: crisisReportService } = await import('../services/crisisReportService');
          
          // Update crisis report with promotion status and link to incident
          // Only update fields that exist in the crisis report schema
          const updateFields = {
            status: 'promoted',
            updatedAt: serverTimestamp()
          };
          
          // Add optional fields if they exist in the schema (check console logs)
          if (crisisReport.hasOwnProperty('promotedByUid') || Object.keys(crisisReport).includes('promotedByUid')) {
            updateFields.promotedByUid = currentUser.uid;
          }
          if (crisisReport.hasOwnProperty('promotedAt') || Object.keys(crisisReport).includes('promotedAt')) {
            updateFields.promotedAt = serverTimestamp();
          }
          if (crisisReport.hasOwnProperty('linkedIncidentId') || Object.keys(crisisReport).includes('linkedIncidentId')) {
            updateFields.linkedIncidentId = docId;
          }
          if (crisisReport.hasOwnProperty('reviewNote') || Object.keys(crisisReport).includes('reviewNote')) {
            updateFields.reviewNote = `Promoted to incident: ${incidentData.title}`;
          }
          
          console.log('🔗 Updating crisis report with fields:', Object.keys(updateFields));
          console.log('🔗 Update data being sent:', updateFields);
          console.log('🔗 Report ID:', crisisReport?.id || fromReportId);
          console.log('🔗 Org ID:', currentUser.orgId);
          
          await crisisReportService.updateReportStatus(
            crisisReport?.id || fromReportId,
            currentUser.orgId,
            updateFields
          );
          
          console.log('✅ Crisis report successfully linked to incident:', docId);
          message.success('Crisis report promoted and linked to new incident');
          
        } catch (linkError) {
          console.error('❌ Failed to link crisis report to incident:', linkError);
          message.warning('Incident created successfully, but failed to link crisis report. You can link it manually later.');
          // Don't block incident creation if linking fails
        }
      }
      
      // 🚨 SEND CMT INCIDENT ALERT SMS (NON-BLOCKING)
      // This runs asynchronously and will not affect incident creation if it fails
      try {
        console.log('🚨 Triggering CMT incident alert SMS...');
        
        // Send CMT alert via Cloud Function (asynchronously, non-blocking)
        fetch('https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/sendCMTIncidentAlert', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            incidentId: docId,
            incidentData: {
              title: incidentData.title,
              crisisType: incidentData.customCrisisType || incidentData.crisisType,
              location: incidentData.location,
              severity: incidentData.severity,
              status: incidentData.status
            },
            orgId: currentUser.orgId,
            cmtId: activeCmt?.id || null,
            userId: currentUser.uid,
            userEmail: currentUser.email
          })
        }).then(async response => {
          if (response.ok) {
            const result = await response.json();

          } else {
            const error = await response.json();
            console.log(`⚠️ CMT incident alert failed: ${error.error || 'Unknown error'}`);
          }
        }).catch(error => {
          console.error('❌ CMT incident alert failed (non-critical):', error);
          // Error is logged but doesn't affect incident creation
        });
        
      } catch (error) {
        console.error('❌ Error triggering CMT alert SMS (non-critical):', error);
        // Error is logged but doesn't affect incident creation
      }
      
      // Create regulatory notification tasks if triggers were selected
      if (selectedRegulatoryTriggers && selectedRegulatoryTriggers.length > 0) {
        console.log('Creating regulatory tasks for new incident...');
        try {
          await createRegulatoryTasks(docId, selectedRegulatoryTriggers, incidentData);

        } catch (taskError) {
          console.error('Error creating regulatory tasks:', taskError);
          // Don't fail the incident creation if task creation fails
        }
      }
      
      // Try to log the creation, but don't fail if logging fails
      try {
        await createIncidentLog({
          incidentId: docId,
          orgId: currentUser.orgId,
          action: INCIDENT_ACTIVITY_TYPES.INCIDENT_CREATED,
          details: `New incident created: "${incident.title}" (${incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType})`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName: createdByName,
          priority: 'high',
          metadata: {
            incidentTitle: incident.title,
            incidentType: incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType,
            status: 'Active',
            severity: incident.severity,
            location: incident.location,
            hasRegulatoryTriggers: selectedRegulatoryTriggers?.length > 0,
            regulatoryTriggersCount: selectedRegulatoryTriggers?.length || 0
          }
        });
      } catch (logError) {
        console.error('Failed to create incident log:', logError);
        // Silently ignore logging errors
      }

      // Generate crisis-specific tasks
      console.log('Generating crisis-specific tasks...');
      setTaskGenerationLoading(true);
      try {
        const crisisType = incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType;
        const tasks = await generateCrisisTasks(crisisType, incidentData, currentUser.orgId, db);
        
        if (tasks && tasks.length > 0) {
          console.log(`Generated ${tasks.length} crisis tasks`);
          setGeneratedTasks(tasks);
          setShowTaskPreview(true);
          // Don't navigate yet - wait for task confirmation
        } else {
          console.log('No crisis tasks generated, navigating to incident details');
          navigate(`/incidents/${docId}`);
        }
      } catch (taskError) {
        console.error('Error generating crisis tasks:', taskError);
        // If task generation fails, still navigate to the newly created incident
        navigate(`/incidents/${docId}`);
      } finally {
        setTaskGenerationLoading(false);
      }
    } catch (err) {
      console.error('Error creating incident:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      setError(`Failed to create incident: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // NUCLEAR OPTION: Use simple HTML date input instead of Ant Design DatePicker
  const handleSimpleDateChange = useCallback((e) => {
    const dateValue = e.target.value; // This will be in YYYY-MM-DD format
    console.log('Simple date changed:', dateValue);
    if (dateValue) {
      const momentDate = moment(dateValue);
      setIncident(prev => ({ ...prev, occurredDate: momentDate }));
    } else {
      setIncident(prev => ({ ...prev, occurredDate: null }));
    }
  }, []);

  // Convert to full text format for display (e.g., "6 September 2025")
  const getSimpleDateValue = () => {
    if (!incident.occurredDate) return '';
    // If it's already in YYYY-MM-DD format, convert to full text
    if (typeof incident.occurredDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(incident.occurredDate)) {
      return moment(incident.occurredDate).format('D MMMM YYYY');
    }
    // Otherwise, convert using moment to full text format
    return moment(incident.occurredDate).format('D MMMM YYYY');
  };

  // Convert to YYYY-MM-DD format for the hidden date input (for form submission)
  const getDateValueForSubmission = () => {
    if (!incident.occurredDate) return '';
    // If it's already in YYYY-MM-DD format, return as-is
    if (typeof incident.occurredDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(incident.occurredDate)) {
      return incident.occurredDate;
    }
    // Otherwise, convert using moment
    return moment(incident.occurredDate).format('YYYY-MM-DD');
  };

  return (
    <div className="p-4">
      <PageHeader title="New Incident" />

      {/* Crisis Report Promotion Banner */}
      {isPromotingFromReport && crisisReport && (
        <Card style={{ marginBottom: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
          <div style={{ padding: '8px 0' }}>
            <Typography.Title level={5} style={{ color: '#52c41a', margin: 0 }}>
              📋 Promoting Crisis Report to Incident
            </Typography.Title>
            <Typography.Text style={{ color: '#389e0d' }}>
              Report {crisisReport.shortRef} from {crisisReport.siteName || 'Unknown site'} — 
              details pre-filled below. Please review before creating the incident.
            </Typography.Text>
          </div>
        </Card>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Required Fields */}
            <div className="mb-4">
              <h3 className="h5 mb-3 fw-bold">Required Information</h3>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Incident Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={incident.title}
                  onChange={(e) => setIncident({ ...incident, title: e.target.value })}
                  required
                  placeholder="e.g. Building fire at Sydney city office building"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Crisis Type *</label>
                <Select
                  className="w-100 crisis-type-select"
                  value={incident.crisisType}
                  onChange={(value) => setIncident({ ...incident, crisisType: value })}
                  required
                  placeholder="Select a crisis type"
                  style={{ textTransform: 'none' }}
                >
                  {INCIDENT_TYPES.map(type => (
                    <Select.Option 
                      key={type} 
                      value={type}
                      style={{ textTransform: 'none' }}
                      className="crisis-type-option"
                    >
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {incident.crisisType === 'Other' && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Specify Crisis Type *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={otherCrisisType}
                    onChange={(e) => setOtherCrisisType(e.target.value)}
                    required
                    placeholder="Please specify the crisis type"
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">Location/Region *</label>
                <LocationInput
                  value={incident.location}
                  onChange={handleLocationChange}
                  placeholder="e.g., Sydney HQ, NSW - Region 2"
                  required
                  className="form-control"
                />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Status *</label>
                    <select
                      className="form-select"
                      value={incident.status}
                      onChange={(e) => setIncident({ ...incident, status: e.target.value })}
                      required
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{capitalize(status)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Severity Rating *</label>
                    <select
                      className="form-select"
                      value={incident.severity}
                      onChange={(e) => setIncident({ ...incident, severity: e.target.value })}
                      required
                    >
                      {SEVERITY_LEVELS.map(level => (
                        <option key={level} value={level}>{capitalize(level)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Incident Occurrence Date/Time */}
              <div className="mb-4">
                <h3 className="h5 mb-3 fw-bold">Incident Occurrence</h3>
                <p className="text-muted mb-3">
                  <small>
                    <strong>Important:</strong> Specify when the incident actually occurred to enable accurate regulatory deadline tracking.
                    If unknown, leave blank and update later when information becomes available.
                  </small>
                </p>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Date Incident Occurred</label>
                      <div style={{ position: 'relative' }}>
                        {/* Display input showing full text format */}
                        <input
                          type="text"
                          className="form-control"
                          value={getSimpleDateValue()}
                          placeholder="Select date..."
                          readOnly
                          style={{ cursor: 'pointer' }}
                          onClick={() => document.getElementById('hiddenDateInput').showPicker?.()}
                        />
                        {/* Hidden date input for actual date selection */}
                        <input
                          id="hiddenDateInput"
                          type="date"
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            opacity: 0, 
                            cursor: 'pointer' 
                          }}
                          value={getDateValueForSubmission()}
                          onChange={handleSimpleDateChange}
                        />
                      </div>
                      <small className="text-muted">When did the incident actually happen?</small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Time Incident Occurred</label>
                      <TimeInput
                        value={incident.occurredTime}
                        onChange={(value) => setIncident({ ...incident, occurredTime: value })}
                        className="form-control"
                      />
                      <small className="text-muted">💡 Tip: Type 1430 or 14:30 or just 14 (becomes 14:00)</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Regulatory Compliance Section */}
            <div className="mb-4">
              <h3 className="h5 mb-3 fw-bold">Regulatory Compliance</h3>
              <RegulatoryTriggerSelector
                regulatoryRules={regulatoryRules}
                incidentType={incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType}
                selectedTriggers={selectedRegulatoryTriggers}
                onChange={setSelectedRegulatoryTriggers}
                showAutoSuggestions={true}
              />
            </div>

            {/* Optional Fields */}
            <div className="mb-4">
              <h3 className="h5 mb-3 fw-bold">Additional Information</h3>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Initial Summary</label>
                <textarea
                  className="form-control"
                  value={incident.summary}
                  onChange={(e) => setIncident({ ...incident, summary: e.target.value })}
                  rows="4"
                  placeholder="Brief situation description (visible to all CMT)"
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/incidents')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
              >
                {loading ? 'Creating...' : 'Create Incident'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Crisis Task Preview Modal */}
      <CrisisTaskPreviewModal
        visible={showTaskPreview}
        onClose={handleTaskPreviewClose}
        onConfirm={handleTaskConfirmation}
        generatedTasks={generatedTasks}
        crisisType={incident.crisisType === 'Other' ? otherCrisisType : incident.crisisType}
        loading={taskGenerationLoading}
      />
    </div>
  );
} 